/**
 * Atividade do usuário no portal — o que o backoffice mostra em "Atividade".
 *
 * Fila em memória, enviada a cada ~5 s e quando a aba some (`visibilitychange`
 * / `pagehide`) com `fetch` + `keepalive` — o mesmo padrão da telemetria de
 * progresso: o lote sobrevive ao fechamento da aba e leva o Bearer, que o
 * `sendBeacon` não leva. Falha é silenciosa: é telemetria, não transação.
 *
 * Sem sessão nada é registrado. O que passa por rota da API (matrícula,
 * curtida, inscrição, pergunta à Centuria) o próprio servidor registra; aqui
 * fica só o que o servidor não vê.
 */
import { useEffect, useRef } from 'react';
import { getAccessTokenSync, userManager } from '@/core/auth';
import { config } from '@/core/config';
import { FLUSH_INTERVAL_MS, type TrackInput, type WireEvent } from './events';
import { createTracker } from './tracker';

export type { ClientAction, TrackInput, TrackedTarget } from './events';
export { createTracker } from './tracker';

function send(batch: WireEvent[]): void {
  const token = getAccessTokenSync();
  if (!token) return;
  void fetch(`${config.apiUrl}${config.apiPrefix}/audit/events`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ events: batch }),
  }).catch(() => {});
}

const tracker = createTracker({ send, isSignedIn: () => Boolean(getAccessTokenSync()) });

/** Registra uma ação. A página é a atual, sem query string (pode ter o termo de uma busca). */
export function track(input: TrackInput): void {
  tracker.track(input, input.path ?? window.location.pathname);
}

// ──────────────────────────── Entrada e saída ────────────────────────────

const SESSION_KEY = 'centralone:atividade:sessao';

/** A sessão do Keycloak (`sid`) do token. Opaca; serve só para não repetir a entrada. */
export function sessionOf(token: string | undefined): string | undefined {
  if (!token) return undefined;
  try {
    const payload = token.split('.')[1] ?? '';
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { sid?: unknown };
    return typeof claims.sid === 'string' ? claims.sid : undefined;
  } catch {
    return undefined;
  }
}

function remembered(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function remember(sid: string | undefined): void {
  try {
    if (sid) localStorage.setItem(SESSION_KEY, sid);
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // Sem armazenamento, a entrada pode se repetir ao recarregar — aceitável.
  }
}

/**
 * Registra a entrada uma vez por sessão do Keycloak. `method` vem de quem
 * sabe como a pessoa entrou (o formulário de senha); a detecção automática
 * (login social, SSO) não tem método.
 */
export function trackLogin(method?: string): void {
  const sid = sessionOf(getAccessTokenSync());
  if (sid ? remembered() === sid : !method) return;
  remember(sid);
  track({ action: 'session.login', metadata: method ? { method } : undefined });
}

/** Registra a saída e envia na hora — depois do `signOut` não há mais token. */
export function trackLogout(): void {
  track({ action: 'session.logout' });
  tracker.flush();
  remember(undefined);
}

// ──────────────────────────── Ciclo de vida ────────────────────────────

/** Espera para a detecção automática, para quem chamou `trackLogin(método)` vir primeiro. */
const LOGIN_DETECTION_DELAY_MS = 1_500;

/** Liga o envio periódico. Chamado uma vez, na raiz do app; devolve o desligamento. */
export function startTracking(): () => void {
  const timer = window.setInterval(() => tracker.flush(), FLUSH_INTERVAL_MS);
  const onHidden = () => {
    if (document.visibilityState === 'hidden') tracker.flush();
  };
  const onPageHide = () => tracker.flush();
  let detection: number | undefined;
  // Entradas que não passam pelo formulário (login social, SSO): a sessão
  // nova aparece aqui. Renovar o token mantém o `sid` e não repete nada.
  const onUserLoaded = () => {
    window.clearTimeout(detection);
    detection = window.setTimeout(() => trackLogin(), LOGIN_DETECTION_DELAY_MS);
  };

  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  userManager.events.addUserLoaded(onUserLoaded);
  onUserLoaded();

  return () => {
    window.clearInterval(timer);
    window.clearTimeout(detection);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', onPageHide);
    userManager.events.removeUserLoaded(onUserLoaded);
    tracker.flush();
  };
}

/**
 * Registra que algo foi aberto (produto, curso, evento) — uma vez por alvo
 * enquanto o componente estiver na tela. `null` enquanto não há o que registrar.
 */
export function useTrackView(input: TrackInput | null): void {
  const latest = useRef(input);
  useEffect(() => {
    latest.current = input;
  });
  const key = input ? `${input.action}|${input.targetId ?? ''}` : null;
  useEffect(() => {
    if (key && latest.current) track(latest.current);
  }, [key]);
}
